import {
    getApiError,
    getTaskListsThunk,
    getWorkStartAndGetScheduleThunk,
} from "tm-shared/api";

import { useInitRetryRequests } from "@/features/appInit/hooks/useInitRetryRequests";
import { useAsyncAction } from "@/hooks";
import { useCheckYesterdayOpenCheckouts } from "@/hooks/app/useCheckYesterdayOpenCheckouts";
import { Queue } from "@/hooks/core/useAsyncTaskQueue";
import { useAppDispatch, useAppSelector } from "@/hooks/redux";
import {
    getKpiGetPointProgressesThunk,
    getPlansByPointIdThunk,
    setIsInitialDataLoading,
} from "@/store/slices/offline";
import { setInitFailedRequests } from "@/store/slices/ui";

import { WorkPlanCapabilities } from "../../WorkPlan.policy";

interface UseStartWorkFlowParams {
    capabilities: WorkPlanCapabilities;
    queue: Queue;
    onOpenDayStartedModal: () => void;
    onSendLocation: () => Promise<void>;
}

export const useStartWorkFlow = ({
    capabilities,
    queue,
    onOpenDayStartedModal,
    onSendLocation,
}: UseStartWorkFlowParams) => {
    const dispatch = useAppDispatch();
    const { handleCheck: handleCheckYesterdayOpenCheckouts } =
        useCheckYesterdayOpenCheckouts();

    const userId = useAppSelector((s) => s.user.id);
    const routeUuid = useAppSelector((s) => s.user.route?.uuid);
    const initFailedRequests = useAppSelector((s) => s.ui.initFailedRequests);
    const initRequests = useInitRetryRequests();

    return useAsyncAction(async () => {
        let shouldStopStartFlow = false;

        queue.clear();

        queue.add("Завантаження розкладу роботи", async () => {
            try {
                const { result } = await dispatch(
                    getWorkStartAndGetScheduleThunk({ userId }),
                ).unwrap();

                const pointIds = Object.keys(result?.points ?? {});

                if (pointIds.length) {
                    await queue.add("Завантаження інформації про задачі", () =>
                        dispatch(
                            getTaskListsThunk({ targetIds: pointIds }),
                        ).unwrap(),
                    );

                    if (capabilities.canLoadPlansAndProgressesOnStart) {
                        if (routeUuid) {
                            await queue.add(
                                "Завантаження інформації про плани на ТТ",
                                async () => {
                                    await dispatch(
                                        getPlansByPointIdThunk({
                                            pointIds,
                                            routeId: routeUuid,
                                        }),
                                    );
                                },
                            );
                        }

                        queue.add(
                            "Завантаження інформації про факти на ТТ",
                            () =>
                                dispatch(
                                    getKpiGetPointProgressesThunk({
                                        pointIds,
                                    }),
                                ),
                        );

                        handleCheckYesterdayOpenCheckouts();
                    }
                }
            } catch (error) {
                const normalizeError = getApiError(error);

                if (normalizeError.code === "-32400") {
                    shouldStopStartFlow = true;
                    onOpenDayStartedModal();
                    return;
                }

                throw error;
            }
        });

        initFailedRequests.forEach((type) => {
            const cmd = initRequests[type];
            if (!cmd) return;

            queue.add(cmd.title, cmd.run);
        });

        await queue.runParallel();

        dispatch(setIsInitialDataLoading(false));

        if (shouldStopStartFlow) {
            return;
        }

        dispatch(setInitFailedRequests([]));
        await onSendLocation();
    });
};
